import React from 'react';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';
import { Phone, Mail, MapPin, Clock, ArrowRight, CheckCircle2 } from 'lucide-react';
import { contactInfo } from '../data';

export default function Kontakt() {
  return (
    <>
      <SEO 
        title="Kontakt"
      />

      {/* Main Content Section */}
      <section className="pt-12 sm:pt-16 pb-16 sm:pb-20 bg-slate-50">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-8 lg:px-12">
          
          {/* Page Header */}
          <div className="mb-12 max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight leading-[1.15] mb-6">
              Kontakt os
            </h1>
            <p className="text-lg sm:text-xl text-slate-700 leading-relaxed font-medium mb-3">
              Vi glæder os til at høre om dit projekt.
            </p>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
              Har du spørgsmål eller ønsker du et uforpligtende tilbud, er du altid velkommen til at kontakte os. Du kan ringe, sende en e-mail eller udfylde kontaktformularen nedenfor – så vender vi tilbage hurtigst muligt.
            </p>
          </div>

          {/* 3 Information Boxes at the Top */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            <div className="flex gap-5 p-6 sm:p-8 bg-white rounded-3xl border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-orange-500 rounded-2xl flex items-center justify-center shrink-0 shadow-md shadow-orange-500/20">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-extrabold text-slate-900 mb-2 tracking-tight">Firmaoplysninger</h3>
                <p className="text-slate-600 font-medium">{contactInfo.address.split(',')[0]}</p>
                <p className="text-slate-600">{contactInfo.address.split(',')[1]?.trim()}</p>
                <a 
                  href={`https://www.google.com/maps/dir//${encodeURIComponent(contactInfo.address)}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-orange-500 font-bold mt-3 hover:text-orange-600 transition-colors text-sm sm:text-base group"
                >
                  Rutevejledning <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
              </div>
            </div>

            <div className="flex gap-5 p-6 sm:p-8 bg-white rounded-3xl border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-teal-800 rounded-2xl flex items-center justify-center shrink-0 shadow-md shadow-teal-800/20">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-extrabold text-slate-900 mb-2 tracking-tight">Kontaktoplysninger</h3>
                <p className="text-slate-600 mb-1">Tlf: <a href={`tel:${contactInfo.phone.replace(/\s+/g, '')}`} className="font-bold text-slate-900 hover:text-orange-500 transition-colors">{contactInfo.phone}</a></p>
                <p className="text-slate-600 mb-2"><a href={`mailto:${contactInfo.email}`} className="font-bold text-slate-900 hover:text-orange-500 transition-colors break-all">{contactInfo.email}</a></p>
                <p className="text-slate-500 text-sm font-medium">CVR: {contactInfo.cvr}</p>
              </div>
            </div>

            <div className="flex gap-5 p-6 sm:p-8 bg-white rounded-3xl border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-teal-950 rounded-2xl flex items-center justify-center shrink-0 shadow-md shadow-teal-950/20">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-extrabold text-slate-900 mb-2 tracking-tight">Telefontider</h3>
                <p className="text-slate-600">Man - tor: <span className="font-semibold text-slate-800">{contactInfo.hours.mon_thu}</span></p>
                <p className="text-slate-600">Fredag: <span className="font-semibold text-slate-800">{contactInfo.hours.fri}</span></p>
                <p className="text-slate-600">Lørdag & søndag: <span className="font-semibold text-slate-800">{contactInfo.hours.sat_sun.toLowerCase()}</span></p>
                <p className="text-slate-500 italic text-sm mt-2">Mails besvares alle dage.</p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Full-width Dark Contact Form Section */}
      <section id="kontakt-sektion" className="bg-teal-900 py-16 sm:py-24 border-t border-teal-800 w-full">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div>
              <div className="inline-block bg-orange-500/10 text-orange-400 px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wider mb-6 border border-orange-500/20">
                Kontakt os i dag for en uforpligtende snak om din opgave.
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-8 leading-[1.15] tracking-tight">
                Få et uforpligtende tilbud på malerarbejde
              </h2>
              <div className="prose prose-lg text-teal-100/90 space-y-6 mb-10">
                <p className="text-base sm:text-lg leading-relaxed">
                  Når du kontakter Syddansk Malerfirma, må du meget gerne beskrive opgaven så konkret som muligt. Det gør det lettere at vurdere arbejdet og vende tilbage med den rette rådgivning.
                </p>
                <p className="text-base sm:text-lg leading-relaxed">
                  Skriv gerne, hvilke rum eller områder der skal males, og om det drejer sig om vægge, lofter, træværk, facade eller andet malerarbejde. Du må også gerne nævne cirka størrelse, nuværende stand, ønsket tidspunkt, og om der er særlige forhold, vi skal være opmærksomme på.
                </p>
                <p className="text-base sm:text-lg leading-relaxed">
                  Jo flere oplysninger vi får fra start, desto nemmere er det at give dig et godt og realistisk svar uden unødvendige spørgsmål frem og tilbage. Du er altid velkommen til at bruge kontaktformularen eller ringe direkte, hvis du vil tage en snak om opgaven.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="bg-teal-950/60 p-6 rounded-2xl shadow-sm border border-teal-800/80">
                  <p className="text-xs font-bold text-orange-400 uppercase tracking-widest mb-2">Ring til os</p>
                  <a href={`tel:${contactInfo.phone.replace(/\s+/g, '')}`} className="text-xl sm:text-2xl font-bold text-white hover:text-orange-400 transition-colors">
                    {contactInfo.phone}
                  </a>
                </div>
                <div className="bg-teal-950/60 p-6 rounded-2xl shadow-sm border border-teal-800/80">
                  <p className="text-xs font-bold text-orange-400 uppercase tracking-widest mb-2">Skriv til os</p>
                  <a href={`mailto:${contactInfo.email}`} className="text-base sm:text-lg font-bold text-white hover:text-orange-400 transition-colors break-words">
                    {contactInfo.email}
                  </a>
                </div>
              </div>

              <div className="mt-10 flex items-center gap-4">
                <div className="bg-white px-5 py-2.5 rounded-xl flex items-center justify-center h-16 shadow-md shrink-0">
                  <img 
                    src="https://assets.auxodev.dk/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/9a931652-82a1-42a7-bfb8-d6f9d61651ab.png" 
                    alt="Syddansk Malerfirma Logo" 
                    className="h-10 w-auto object-contain" 
                  />
                </div>
                <div className="h-8 w-px bg-teal-800 shrink-0"></div>
                <p className="text-teal-200/80 font-medium text-sm sm:text-base">Din lokale maler i Sønderborg & omegn</p>
              </div>
            </div>
            
            <div className="bg-teal-950/60 p-6 sm:p-10 rounded-3xl shadow-2xl border border-teal-800/80 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-bl-full -mr-16 -mt-16 pointer-events-none"></div>
              <div className="relative z-10">
                <h3 className="text-2xl sm:text-3xl font-black text-white mb-3 tracking-tight leading-[1.15]">Få et uforpligtende tilbud</h3>
                <p className="text-teal-100/80 mb-8 text-base sm:text-lg">Udfyld formularen herunder og vi vender tilbage med et uforpligtende tilbud inden for 48 timer.</p>
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
